import axios from 'axios';

// One client for the Google My Business backend. The session cookie is HttpOnly; the
// XSRF cookie name is unique per Auto Appy app. Every request carries the
// chosen organisation so the backend can check membership.
const xsrfCookieName = process.env.REACT_APP_XSRF_COOKIE || 'autoappy_gmb_xsrf';
export const BASE_URL = (process.env.REACT_APP_BASE_URL || 'http://localhost:8890').replace(/\/+$/, '');

const api = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  withXSRFToken: true,
  xsrfCookieName,
  xsrfHeaderName: 'X-XSRF-TOKEN',
  headers: { Accept: 'application/json' },
});

let organisationId = '';
export const setOrganisation = (id) => { organisationId = id || ''; };
export const currentOrganisation = () => organisationId;

api.interceptors.request.use((request) => {
  if (organisationId && !request.headers['X-AutoAppy-Workspace']) request.headers['X-AutoAppy-Workspace'] = organisationId;
  return request;
});

let csrfReady;
export const ensureCsrf = () => {
  if (!csrfReady) csrfReady = api.get('/sanctum/csrf-cookie').catch((error) => { csrfReady = undefined; throw error; });
  return csrfReady;
};

api.interceptors.response.use((response) => response, async (error) => {
  const request = error.config;
  // An expired CSRF token (419) is renewed once; nothing else is retried.
  if (error.response?.status === 419 && request && !request.__csrfRetried) {
    request.__csrfRetried = true;
    csrfReady = undefined;
    await ensureCsrf();
    return api(request);
  }
  return Promise.reject(error);
});

/** A sentence for the person, from any API error. */
export function errorText(error, fallback = 'Something went wrong. Please try again.') {
  const data = error?.response?.data;
  if (data?.errors && typeof data.errors === 'object') {
    const first = Object.values(data.errors).flat()[0];
    if (first) return String(first);
  }
  if (data?.message && !/^(Server Error|Unauthenticated\.?)$/i.test(data.message)) return data.message;
  if (!error?.response) return 'Cannot reach Auto Appy. Check your internet connection.';
  if (error.response.status === 403) return 'You do not have permission to do this.';
  if (error.response.status === 429) return 'Too many changes at once. Wait a minute and try again.';
  return fallback;
}

/** Google's own detail (status, reason, fields, reconnect / not-approved hints) when the backend passed it on. */
export const googleDetail = (error) => error?.response?.data?.google || null;

export async function get(path, params) {
  const response = await api.get(path, { params });
  return response.data;
}

export async function send(method, path, body) {
  await ensureCsrf();
  const response = await api.request({ method, url: path, data: body });
  return response.data;
}

export const post = (path, body) => send('post', path, body);
export const put = (path, body) => send('put', path, body);
export const patch = (path, body) => send('patch', path, body);
export const del = (path) => send('delete', path);

export async function upload(path, fields, onProgress) {
  await ensureCsrf();
  const form = new FormData();
  Object.entries(fields).forEach(([key, value]) => { if (value !== undefined && value !== null) form.append(key, value); });
  const response = await api.post(path, form, { onUploadProgress: (event) => onProgress?.(event.total ? event.loaded / event.total : 0) });
  return response.data;
}

/** A link to a backend file or download that needs the organisation. */
export function backendUrl(path, params = {}) {
  const query = new URLSearchParams({ ...params, ...(organisationId ? { workspace: organisationId } : {}) });
  return `${BASE_URL}${path}${query.toString() ? `${path.includes('?') ? '&' : '?'}${query}` : ''}`;
}

export default api;
